/**
 * Service de gestion des catégories
 * Permet de créer, lire, mettre à jour et supprimer des catégories
 */

import { supabase } from '../supabaseClient';
import { getImpersonatedUserIdFromStorage } from '../../contexts/ImpersonationContext';

export interface Categorie {
  id?: string;
  user_id?: string;
  nom: string;
  type: 'depense' | 'revenu';
  couleur: string;
  /** Clé catalogue (ex. home, gift) — nullable pour rétrocompat */
  icone?: string | null;
  commentaire?: string;
  created_at?: string;
  updated_at?: string;
}

/**
 * Récupère toutes les catégories de l'utilisateur connecté
 */
export const getCategories = async (): Promise<{ data: Categorie[] | null; error: any }> => {
  try {
    const impersonatedUserId = getImpersonatedUserIdFromStorage();
    let data: any = null; let error: any = null;
    if (impersonatedUserId) {
      const rpc = await supabase.rpc('admin_get_categories', { p_user_id: impersonatedUserId });
      data = rpc.data; error = rpc.error;
    } else {
      const res = await supabase
        .from('categories')
        .select('*')
        .order('nom', { ascending: true });
      data = res.data; error = res.error;
    }

    if (error) {
      console.error('Erreur lors de la récupération des catégories:', error);
      return { data: null, error };
    }

    return { data, error: null };
  } catch (error) {
    console.error('Exception lors de la récupération des catégories:', error);
    return { data: null, error };
  }
};

/**
 * Récupère les catégories par type (dépense ou revenu)
 */
export const getCategoriesByType = async (type: 'depense' | 'revenu'): Promise<{ data: Categorie[] | null; error: any }> => {
  try {
    const impersonatedUserId = getImpersonatedUserIdFromStorage();
    let data: any = null; let error: any = null;
    if (impersonatedUserId) {
      // RPC renvoie toutes les catégories, filtrer par type côté client
      const rpc = await supabase.rpc('admin_get_categories', { p_user_id: impersonatedUserId });
      if (!rpc.error) {
        data = (rpc.data as any[]).filter(c => c.type === type).sort((a, b) => a.nom.localeCompare(b.nom));
      } else {
        error = rpc.error;
      }
    } else {
      const res = await supabase
        .from('categories')
        .select('*')
        .eq('type', type)
        .order('nom', { ascending: true });
      data = res.data; error = res.error;
    }

    if (error) {
      console.error('Erreur lors de la récupération des catégories par type:', error);
      return { data: null, error };
    }

    return { data, error: null };
  } catch (error) {
    console.error('Exception lors de la récupération des catégories par type:', error);
    return { data: null, error };
  }
};

/**
 * Crée une nouvelle catégorie pour l'utilisateur connecté
 */
export const createCategorie = async (categorie: Categorie): Promise<{ data: Categorie | null; error: any }> => {
  try {
    // Récupérer l'ID de l'utilisateur connecté
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      return { data: null, error: 'Utilisateur non connecté' };
    }

    // Créer la catégorie avec l'ID de l'utilisateur
    const { data, error } = await supabase
      .from('categories')
      .insert([
        {
          ...categorie,
          user_id: user.id,
        },
      ])
      .select()
      .single();

    if (error) {
      console.error('Erreur lors de la création de la catégorie:', error);
      return { data: null, error };
    }

    return { data, error: null };
  } catch (error) {
    console.error('Exception lors de la création de la catégorie:', error);
    return { data: null, error };
  }
};

/**
 * Met à jour une catégorie existante
 */
export const updateCategorie = async (id: string, updates: Partial<Categorie>): Promise<{ data: Categorie | null; error: any }> => {
  try {
    const { data, error } = await supabase
      .from('categories')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Erreur lors de la mise à jour de la catégorie:', error);
      return { data: null, error };
    }

    return { data, error: null };
  } catch (error) {
    console.error('Exception lors de la mise à jour de la catégorie:', error);
    return { data: null, error };
  }
};

/**
 * Supprime une catégorie existante
 */
export const deleteCategorie = async (id: string): Promise<{ success: boolean; error: any }> => {
  try {
    // Vérification que l'ID est valide
    if (!id || id.trim() === '') {
      console.error('ID de catégorie invalide ou vide');
      return { success: false, error: 'ID de catégorie invalide' };
    }
    
    // Suppression directe de la catégorie
    const { error } = await supabase
      .from('categories')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Erreur lors de la suppression de la catégorie:', error);
      return { success: false, error };
    }

    return { success: true, error: null };
  } catch (error) {
    console.error('Exception lors de la suppression de la catégorie:', error);
    return { success: false, error };
  }
};

/**
 * Récupère une catégorie par son ID
 */
export const getCategorieById = async (id: string): Promise<{ data: Categorie | null; error: any }> => {
  try {
    const { data, error } = await supabase
      .from('categories')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      console.error('Erreur lors de la récupération de la catégorie:', error);
      return { data: null, error };
    }

    return { data, error: null };
  } catch (error) {
    console.error('Exception lors de la récupération de la catégorie:', error);
    return { data: null, error };
  }
};

/**
 * Vérifie si une catégorie est utilisée dans des transactions
 */
export const isCategorieUsed = async (id: string): Promise<{ isUsed: boolean; error: any }> => {
  try {
    const { data, error } = await supabase
      .from('transactions')
      .select('id')
      .eq('categorie_id', id)
      .limit(1);

    if (error) {
      console.error('Erreur lors de la vérification de l\'utilisation de la catégorie:', error);
      return { isUsed: false, error };
    }

    return { isUsed: data && data.length > 0, error: null };
  } catch (error) {
    console.error('Exception lors de la vérification de l\'utilisation de la catégorie:', error);
    return { isUsed: false, error };
  }
}; 